import React from "react";

import { SerializedStyles } from "@emotion/react";
import {
  toCssSrcSet,
  transformImage,
  TransformImageT,
  transformRasterImageOf,
  wrapToHtmlSourceElements,
} from "./transformImage";
import { pipe } from "fp-ts/lib/function";
import { array, option } from "fp-ts";
import { useImageAssetHintsStateCtx } from "../../cms/edit-overlay/ToolbarStateProvider";
import { ContentfulImageAssetHint } from "./ContentfulImage2";

type ImageProps = React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;
export type ContentfulImageProps = Omit<ImageProps, "src" | "ref"> & {
  contentfulLink: ExtendedContentfulLink;
  attachPictureStyles?: SerializedStyles;
  lazyLoaded?: "lazy" | "eager";
  sizes?: string;
  higherDPI?: Array<number>;
  assetId: string | undefined;
};

export type ExtendedContentfulLink = {
  url: string;
  requestedWidth: number;
  noTransparentBackground?: boolean;
  quality?: number;
};

export const ContentfulImage = React.forwardRef<HTMLImageElement, ContentfulImageProps>((props, ref) => {
  const imageAssetHints = useImageAssetHintsStateCtx();

  const { alt, contentfulLink, attachPictureStyles, lazyLoaded, higherDPI, sizes, loading, assetId, ...rest } = props;

  const loadingProp: "lazy" | "eager" | undefined = lazyLoaded || loading;

  const { transformed, sources } = getSourceElements(contentfulLink, higherDPI);

  if (typeof transformed === "string") {
    return <img src={transformed} alt={alt} {...rest} ref={ref} loading={loading} />;
  } else {
    const sizes = props.sizes || (props.higherDPI ? `${transformed.width}px` : undefined);

    return (
      <>
        <picture css={attachPictureStyles}>
          {sources.map(source => {
            return (
              <source sizes={sizes} key={JSON.stringify(source)} srcSet={source.srcSetCssStr} type={source.type} />
            );
          })}

          <img src={transformed.fallback} alt={alt} {...rest} ref={ref} loading={loadingProp} sizes={sizes} />
        </picture>

        {imageAssetHints && assetId && <ContentfulImageAssetHint assetId={assetId} />}
      </>
    );
  }
});

export function getSourceElements(contentfulLink: ContentfulImageProps["contentfulLink"], higherDPI?: Array<number>) {
  const transformed = transformImage(contentfulLink);

  const transformedTimes =
    higherDPI && typeof contentfulLink !== "string" ? getTransformedTimes(contentfulLink, higherDPI) : undefined;

  return {
    transformed: transformed,
    sources: transformedTimes ? getTransformedImages(transformedTimes) : wrapToHtmlSourceElements(transformed),
  };
}

function getTransformedImages(
  transformedTimes: Array<TransformImageT>,
): Array<{ srcSet: Array<{ link: string; width: number }>; srcSetCssStr: string; type: string }> {
  const ts = pipe(
    transformedTimes,
    array.filterMap(t => (t.tag === "transparency-suspected" ? option.some(t) : option.none)),
  );

  if (ts.length > 0) {
    const webps = ts.map(t => ({ link: t.webpLink, width: t.width }));
    const pngs = ts.map(t => ({ link: t.pngLink, width: t.width }));

    return [
      {
        type: "image/webp",
        srcSet: webps,
        srcSetCssStr: toCssSrcSet(webps),
      },
      {
        type: "image/png",
        srcSet: pngs,
        srcSetCssStr: toCssSrcSet(pngs),
      },
    ];
  } else {
    const vs = pipe(
      transformedTimes,
      array.filterMap(t => (t.tag === "visual-image" ? option.some(t) : option.none)),
    );

    const webps = vs.map(t => ({ link: t.webpLink, width: t.width }));
    const jpgs = vs.map(t => ({ link: t.jpgLink, width: t.width }));

    return [
      {
        type: "image/webp",
        srcSet: webps,
        srcSetCssStr: toCssSrcSet(webps),
      },
      {
        type: "image/jpg",
        srcSet: jpgs,
        srcSetCssStr: toCssSrcSet(jpgs),
      },
    ];
  }
}

function getTransformedTimes(extendedContentfulLink: ExtendedContentfulLink, higherDPI: Array<number>) {
  const add = higherDPI.flatMap(t =>
    t > 0
      ? [
          transformRasterImageOf(
            extendedContentfulLink.url,
            extendedContentfulLink,
            extendedContentfulLink.requestedWidth * t,
          ),
        ]
      : [],
  );

  return [
    transformRasterImageOf(extendedContentfulLink.url, extendedContentfulLink, extendedContentfulLink.requestedWidth),
    ...add,
  ];
}
